/**
 * Company service - API calls for company list
 * Mirrors Angular company-list.component.ts API interactions
 */
import apiClient from '@/lib/api/apiClient'
import {
  companyUpdateBodyToFormData,
  prepareCompanyUpdateBody,
  logCompanyPostPayloadDev,
} from '@/features/company/utils/companyPayloadUtils'
import type { CompanyConfigResponse, CompanyFilters, CompanyListResponse } from '../types/company'

/**
 * Get company list with filters
 * Angular: this.httpService.get('/api/company', this.urlParams)
 */
export async function getCompanyList(filters: CompanyFilters): Promise<CompanyListResponse> {
    // Build query params, stripping empty values (matches Angular's getFilterValue)
    const params: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(filters)) {
        if (key === 'org_name') continue // session label only (Angular saveFilter)
        if (value !== undefined && value !== null && value !== '') {
            params[key] = value
        }
    }

    const response = await apiClient.get('/api/company', { params })
    return response.data
}

/**
 * Create a new company
 * Angular: this.httpService.post('/api/company', payload)
 */
export async function createCompany(payload: Record<string, unknown> | FormData): Promise<unknown> {
    const response = await apiClient.post('/api/company', payload)
    return response.data
}

/**
 * POST /api/company/:id — same wire format as Angular `editCompany()`: multipart FormData + multiSelect keys.
 * @returns Response body (same as Angular `subscribe((response) => …)`).
 */
export async function postCompanyUpdate(
    companyId: string | number,
    payload: Record<string, unknown>
): Promise<unknown> {
    const body = prepareCompanyUpdateBody(payload)
    logCompanyPostPayloadDev(String(companyId), body)
    const fd = companyUpdateBodyToFormData(body)
    const response = await apiClient.post(`/api/company/${companyId}`, fd)
    return response.data
}

/**
 * Toggle company listed status
 * Angular: this.httpService.post(`/api/company/${company.company_id}`, requestData, { upload: true })
 */
export async function updateCompanyListed(
    companyId: number,
    listed: number,
    orgId: number
): Promise<void> {
    await postCompanyUpdate(companyId, { listed, org_id: orgId })
}

/**
 * Search companies for autocomplete
 * Angular: this.httpService.get('/api/all_company', requestData)
 */
export async function searchCompanies(
    searchText: string = '',
    listed: string = 'all'
): Promise<string[]> {
    const params: Record<string, unknown> = {
        listed,
        page: 'show_listed',
        exact_search: true,
    }
    if (searchText) {
        params.q = searchText
    }
    const response = await apiClient.get('/api/all_company', { params })
    return (response.data || []).map((c: { company_name: string }) => c.company_name)
}

/**
 * Get sales teams
 * Angular: this.httpService.get('/api/sales_team')
 */
export async function getSalesTeams(): Promise<Record<string, unknown>> {
    const response = await apiClient.get('/api/sales_team')
    return response.data
}

/**
 * Get company config (property mgmt systems, resident screening, etc.)
 * Angular: this.httpService.get('/api/company_config')
 */
export async function getCompanyConfig(): Promise<CompanyConfigResponse> {
    const response = await apiClient.get('/api/company_config')
    return response.data
}

/**
 * Get all organizations for Charity Reference dropdown
 * Angular: this.httpService.get('/api/all_organizations', requestData)
 */
export async function getAllOrganizations(searchText: string = ''): Promise<unknown[]> {
    const params: Record<string, unknown> = { all: true }
    if (searchText) {
        params.q = searchText
    }
    const response = await apiClient.get('/api/all_organizations', { params })
    return response.data
}
